import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import {SEGMENT} from "@reva/shared/reva-spec";
import {format, startOfYear, subYears} from "date-fns";

import {MarketDataApiInterface} from "../api/interface/market-data";
import {buildApiQueryError, generateDefaultQueryError, type QueryError} from "../util/errorUtil";
import {selectCredentials} from "./authSlice";
import {selectRevaId} from "./persistenceSlice";
import type {RevaThunkAction} from "./revaHooks";
import type {ApiQueryError} from "./types";

/** Offer statistics for one side (buy/rent) of a market data query, as returned by the `marketdata` endpoint. */
export interface MarketDataQueryResult {
    offerCount: number;
    minPrice?: number;
    maxPrice?: number;
    medianPrice?: number;
    minPriceTotal?: number;
    maxPriceTotal?: number;
    medianPriceTotal?: number;
    queryId?: number;
}

/** Value ranges available for the current segment, used as input placeholders/limits. */
export interface MarketDataConstraints {
    spaceLivingMax?: number;
    spaceLivingMin?: number;
    priceMax?: number;
    priceMin?: number;
    rentMax?: number;
    rentMin?: number;
    yearOfConstructionMax?: number;
    yearOfConstructionMin?: number;
    spacePlotMax?: number;
    spacePlotMin?: number;
    dateToMax?: string;
}

/** Query input for the "Marktdaten" tab (`TabMarketData`). */
export interface MarketDataQueryInput {
    segment: string | null;
    buildingType: number[] | null;
    newlyBuild: "new" | "old" | "all";
    areaFilter: string;
    distance: number;
    percentileFrom: number;
    percentileTo: number;
    priceFrom: number | null;
    priceTo: number | null;
    rentFrom: number | null;
    rentTo: number | null;
    spaceLivingFrom: number | null;
    spaceLivingTo: number | null;
    spacePlotFrom: number | null;
    spacePlotTo: number | null;
    spacePlotIncludeUnknown?: boolean;
    yearOfConstructionFrom: number | null;
    yearOfConstructionTo: number | null;
    yearOfConstructionIncludeUnknown?: boolean;
    dateFrom: string | null;
    dateTo?: string | null;
    round?: boolean;
    addTotal?: boolean;
}

const defaultQueryInput: MarketDataQueryInput = {
    segment: null,
    buildingType: null,
    newlyBuild: "all",
    areaFilter: "postalCodes",
    distance: 1,
    percentileFrom: 5,
    percentileTo: 95,
    priceFrom: null,
    priceTo: null,
    rentFrom: null,
    rentTo: null,
    spaceLivingFrom: null,
    spaceLivingTo: null,
    spacePlotFrom: null,
    spacePlotTo: null,
    spacePlotIncludeUnknown: true,
    yearOfConstructionFrom: null,
    yearOfConstructionTo: null,
    yearOfConstructionIncludeUnknown: true,
    dateFrom: format(startOfYear(subYears(new Date(), 1)), "yyyy-MM-dd"),
};

/** Query result for both sides (buy/rent) of a market data query, as returned by the `marketdata` endpoint. */
export interface MarketDataResult {
    buyQueryResult: MarketDataQueryResult;
    rentQueryResult: MarketDataQueryResult;
}

export function buildMarketDataQuery(queryInput: MarketDataQueryInput): Partial<MarketDataQueryInput> {
    if (queryInput.segment !== SEGMENT.WHG_K) return queryInput;
    const {spacePlotFrom, spacePlotTo, spacePlotIncludeUnknown, ...queryWithoutSpacePlot} = queryInput;
    return queryWithoutSpacePlot;
}

export const marketDataInputSlice = createSlice({
    name: "marketDataInput",
    initialState: {
        queryInput: defaultQueryInput,
        result: null as MarketDataResult | null,
        constraints: null as MarketDataConstraints | null,
        queryError: null as ApiQueryError | null,
        loading: false,
    },
    reducers: {
        setMarketDataInputField: (state, action: PayloadAction<{key: keyof MarketDataQueryInput; value: unknown}>) => {
            const {key, value} = action.payload;
            (state.queryInput as Record<string, unknown>)[key] = value;
            // result is only valid for the query it was fetched for; invalidate on any input change
            state.result = null;
            state.loading = false;
            if (key === "segment") {
                state.queryInput.buildingType = null;
                state.constraints = null;
                if (value === SEGMENT.WHG_K) {
                    state.queryInput.spacePlotFrom = null;
                    state.queryInput.spacePlotTo = null;
                    state.queryInput.spacePlotIncludeUnknown = true;
                }
            }
        },
        marketDataLoadingStarted: (state) => {
            state.loading = true;
            state.queryError = null;
        },
        /** Store a fetched query result. `constraints` is omitted when it wasn't part of this fetch. */
        setMarketDataResult: (
            state,
            action: PayloadAction<{result: MarketDataResult; constraints?: MarketDataConstraints}>,
        ) => {
            state.result = action.payload.result;
            state.loading = false;
            if (action.payload.constraints !== undefined) state.constraints = action.payload.constraints;
        },
        setMarketDataQueryError: (state, action: PayloadAction<QueryError | null>) => {
            state.queryError = action.payload ? buildApiQueryError(action.payload) : null;
            state.loading = false;
        },
        resetMarketDataInput: (state) => {
            state.queryInput = defaultQueryInput;
            state.result = null;
            state.constraints = null;
            state.queryError = null;
            state.loading = false;
        },
    },
    selectors: {
        selectMarketDataQueryInput: (state) => state.queryInput,
        selectMarketDataResult: (state) => state.result,
        selectMarketDataConstraints: (state) => state.constraints,
        selectMarketDataError: (state) => state.queryError,
        selectMarketDataLoading: (state) => state.loading,
    },
});

export const {setMarketDataInputField, setMarketDataResult, setMarketDataQueryError, resetMarketDataInput} =
    marketDataInputSlice.actions;

export const {
    selectMarketDataQueryInput,
    selectMarketDataResult,
    selectMarketDataConstraints,
    selectMarketDataError,
    selectMarketDataLoading,
} = marketDataInputSlice.selectors;

/**
 * Fragt die Marktdaten zur aktuellen Eingabe ab, sofern noch kein Ergebnis da ist und keine Abfrage läuft.
 * Ändert sich die Eingabe während der Abfrage, wird deren Antwort verworfen.
 */
export const loadMarketData =
    (): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {serviceApi}) => {
        const queryInput = selectMarketDataQueryInput(getState());
        const credentials = selectCredentials(getState());
        const revaId = selectRevaId(getState());
        if (queryInput.segment == null || selectMarketDataResult(getState()) != null) return;
        if (selectMarketDataLoading(getState()) || !credentials || !revaId) return;

        const includeConstraints = selectMarketDataConstraints(getState()) == null;
        const isCurrent = () => selectMarketDataQueryInput(getState()) === queryInput;
        dispatch(marketDataInputSlice.actions.marketDataLoadingStarted());
        try {
            const response = await new MarketDataApiInterface(revaId, {
                ...buildMarketDataQuery(queryInput),
                includeConstraints,
            }).execute(serviceApi, credentials);
            if (!isCurrent()) return;
            if (response?.buyQueryResult == null) {
                dispatch(setMarketDataQueryError({error: "general.error.invalid", errorHasContactLink: true}));
                return;
            }
            dispatch(
                setMarketDataResult({
                    result: {buyQueryResult: response.buyQueryResult, rentQueryResult: response.rentQueryResult},
                    ...(includeConstraints ? {constraints: response.constraints} : {}),
                }),
            );
        } catch (err) {
            if (isCurrent()) dispatch(setMarketDataQueryError(generateDefaultQueryError(err as Error)));
        }
    };

export default marketDataInputSlice.reducer;
